import React from 'react';
import { useNavigate } from 'react-router-dom';

// Import local assets
import cloutLogo from '../assets/logos/clout-logo.png';
import womanProfessional from '../assets/images/woman-professional.png';
import teamBackground from '../assets/images/team-background.png';
import doctorIcon from '../assets/icons/doctor-icon.svg';
import firstAidIcon from '../assets/icons/first-aid-icon.svg';

// Import components
import Footer from '../components/Landing/Footer';
import JoinMovementCard from '../components/Landing/JoinMovementCard';

const WhoWeAre: React.FC = () => {
  const navigate = useNavigate();

  const handleRedirectToSignIn = () => {
    navigate('/signin');
  };

  const handleSignInClick = () => {
    navigate('/signin');
  };

  return (
    <div className="relative w-full">
      {/* Header with glass effect */}
      <header className="fixed backdrop-blur-[5px] backdrop-filter bg-[rgba(248,246,242,0.8)] box-border content-stretch flex flex-col gap-0 items-center justify-center left-0 right-0 px-4 md:px-10 lg:px-20 py-4 top-0 w-full z-50 shadow-lg">
        <div className="flex flex-col items-center gap-1">
          <img
            src={cloutLogo}
            alt="CLOUT Aesthetics - Healthcare Provider Review Platform"
            className="h-[40px] md:h-[50px] w-[140px] md:w-[180px] object-contain cursor-pointer"
            onClick={() => navigate('/')}
            loading="lazy"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full pt-16 md:pt-20">
        {/* Hero Section */}
        <section className="w-full relative bg-[#f8f6f2] min-h-[500px] md:min-h-[600px] lg:min-h-[700px] overflow-hidden">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 h-full">
            <div className="flex flex-col lg:flex-row items-center justify-between h-full py-8 lg:py-12">
              {/* Left Content */}
              <div className="flex flex-col gap-6 lg:gap-8 items-start justify-center text-[#1f1f1f] w-full lg:w-1/2 lg:pr-8">
                <h1 className="font-['Ovo',_serif] text-[32px] md:text-[48px] lg:text-[56px] xl:text-[64px] text-center lg:text-left uppercase leading-tight">
                  WHO WE ARE?
                </h1>
                <p className="font-['Ovo',_serif] font-normal text-[16px] md:text-[18px] lg:text-[20px] xl:text-[22px] text-center lg:text-left leading-relaxed max-w-[600px]">
                  CLOUT Aesthetics is the aesthetic authority for providers and businesses. We are
                  revolutionizing aesthetic medicine by combining reviews, education, experience,
                  and online presence into a comprehensive CLOUT Score- your badge of authority in
                  the aesthetics industry.
                </p>
              </div>

              {/* Right Image */}
              <div className="w-full lg:w-1/2 h-[400px] md:h-[500px] lg:h-[600px] xl:h-[700px] relative mt-8 lg:mt-0">
                <img
                  src={womanProfessional}
                  alt="Professional woman in healthcare"
                  className="w-full h-full object-cover object-center rounded-lg lg:rounded-none"
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </section>
        {/* Team Background Section - Proper Height */}
        <section className="w-full relative h-[500px] md:h-[600px] lg:h-[700px] overflow-hidden">
          <div
            className="absolute inset-0 bg-center bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url('${teamBackground}')`,
            }}
          />
          {/* Reduced overlay opacity to show more of the background */}
          <div className="absolute inset-0 bg-black bg-opacity-10" />
        </section>

        {/* Join the Movement Card - Positioned at bottom of background section */}
        <div className="relative -mt-32 md:-mt-36 lg:-mt-40 mb-40 md:mb-48 lg:mb-56 z-10">
          <JoinMovementCard onJoinClick={handleRedirectToSignIn} />
        </div>

        {/* Our Mission Section */}
        <section className="w-full py-4 md:py-8 lg:py-12 bg-white">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col gap-12 md:gap-16 lg:gap-20 items-center justify-center">
              <h2 className="font-['Ovo',_serif] text-[#1f1f1f] text-[32px] md:text-[40px] lg:text-[48px] xl:text-[56px] text-center uppercase leading-tight">
                OUR MISSION
              </h2>

              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 xl:gap-16 items-stretch justify-center w-full max-w-[1200px]">
                {/* For Providers */}
                <div className="flex flex-col gap-6 md:gap-8 items-center text-center flex-1">
                  <div className="bg-[#f8f6f2] flex items-center justify-center h-[160px] md:h-[180px] lg:h-[200px] w-[160px] md:w-[180px] lg:w-[200px] rounded-full">
                    <img
                      alt="Doctor icon representing healthcare providers"
                      className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16"
                      loading="lazy"
                      src={doctorIcon}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>

                  <div className="flex flex-col gap-4">
                    <h3 className="font-['Ovo',_serif] text-[#1f1f1f] text-[20px] md:text-[24px] lg:text-[28px] xl:text-[32px] uppercase font-semibold">
                      FOR PROVIDERS
                    </h3>
                    <p className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[14px] md:text-[16px] lg:text-[18px] leading-relaxed max-w-[400px]">
                      More than a Score - A Trusted Community.
                      <br />
                      <br />
                      Join a movement that celebrates integrity and elevates excellence in aesthetic
                      medicine.
                    </p>
                  </div>
                </div>

                {/* For the Industry */}
                <div className="flex flex-col gap-6 md:gap-8 items-center text-center flex-1">
                  <div className="bg-[#f8f6f2] flex items-center justify-center h-[160px] md:h-[180px] lg:h-[200px] w-[160px] md:w-[180px] lg:w-[200px] rounded-full">
                    <img
                      alt="First aid icon representing industry standards"
                      className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16"
                      loading="lazy"
                      src={firstAidIcon}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>

                  <div className="flex flex-col gap-4">
                    <h3 className="font-['Ovo',_serif] text-[#1f1f1f] text-[20px] md:text-[24px] lg:text-[28px] xl:text-[32px] uppercase font-semibold">
                      FOR THE INDUSTRY
                    </h3>
                    <p className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[14px] md:text-[16px] lg:text-[18px] leading-relaxed max-w-[400px]">
                      Revolutionizing the Industry - CLOUT Aesthetics sets the standard for
                      providers and businesses in the aesthetic industry.
                    </p>
                  </div>
                </div>
              </div>

              <button
                className="border-2 border-[#1f1f1f] border-solid px-6 md:px-8 lg:px-10 py-3 md:py-4 font-['Ovo',_serif] text-[#1f1f1f] text-[16px] md:text-[18px] lg:text-[20px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-all duration-300 cursor-pointer rounded-md font-semibold tracking-wide"
                onClick={handleRedirectToSignIn}
              >
                Get Started
              </button>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <Footer onSignInClick={handleSignInClick} />
    </div>
  );
};

export default WhoWeAre;
